// Adding a material (or a variety of one) from the order panel.
import type { MaterialRateItem } from '../pages/OfficeEstimationPage';

export type NewMaterialInput = {
  /** The group as stored on materials: a built-in key (rmc) or a typed name. */
  group: string;
  materialName: string;
  unit: string;
  /** Optional: what kind of material, and which variety of it. */
  assemblyType: string;
  variety: string;
};

const norm = (value?: string) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

/** The library document for a new material. It has no vendor quotes yet. */
export function buildMaterialPayload(input: NewMaterialInput, order: number): Record<string, any> {
  const name = input.materialName.trim();
  const assembly = input.assemblyType.trim();
  const variety = input.variety.trim();
  const mapped = assembly || variety;
  return {
    group: input.group,
    materialName: name,
    grade: '',
    brand: '',
    unit: input.unit,
    secondaryUnitRequired: false,
    secondaryMultiplier: 0,
    secondaryUnit: '',
    codeRequired: false,
    assemblyVarietyMap: mapped ? [{ id: `map_${Date.now().toString(36)}`, assemblyType: assembly || name, varieties: variety ? [variety] : [], type: 'base', unit: input.unit }] : [],
    assemblyTypes: assembly ? [assembly] : [],
    varieties: variety ? [variety] : [],
    quoteOptionTemplates: [],
    diameter: '',
    coveredDiameters: [],
    quotes: [],
    order,
  };
}

/** Materials whose name is the same as, or contains, or is contained in, the typed one. */
export function similarMaterials(items: MaterialRateItem[], name: string): MaterialRateItem[] {
  const wanted = norm(name);
  if (wanted.length < 3) return [];
  return items.filter((item) => item.group !== 'labour').filter((item) => {
    const have = norm(item.materialName);
    return have === wanted || (have.length >= 3 && (have.includes(wanted) || wanted.includes(have)));
  });
}

export const sameMaterialName = (item: MaterialRateItem, name: string) => norm(item.materialName) === norm(name);

/** True when the material already lists this assembly type and variety. */
export function hasVariety(item: MaterialRateItem, assemblyType: string, variety: string): boolean {
  const assembly = norm(assemblyType) || norm(item.materialName);
  const wanted = norm(variety);
  return (item.assemblyVarietyMap || []).some((entry) =>
    norm(entry.assemblyType) === assembly && (wanted ? (entry.varieties || []).some((candidate) => norm(candidate) === wanted) : true));
}

/** The changes that add an assembly type and variety to an existing material. */
export function addVarietyPatch(item: MaterialRateItem, assemblyType: string, variety: string): Record<string, any> {
  const assembly = assemblyType.trim() || item.materialName;
  const value = variety.trim();
  const map = (item.assemblyVarietyMap || []).map((entry) => ({ ...entry, varieties: [...(entry.varieties || [])] }));
  const row = map.find((entry) => norm(entry.assemblyType) === norm(assembly));
  if (row) { if (value && !row.varieties.some((candidate) => norm(candidate) === norm(value))) row.varieties.push(value); }
  else map.push({ id: `map_${Date.now().toString(36)}`, assemblyType: assembly, varieties: value ? [value] : [], type: 'base', unit: item.unit } as any);
  const assemblyTypes = (item.assemblyTypes || []).some((entry) => norm(entry) === norm(assembly)) || norm(assembly) === norm(item.materialName) ? item.assemblyTypes || [] : [...(item.assemblyTypes || []), assembly];
  const varieties = value && !(item.varieties || []).some((entry) => norm(entry) === norm(value)) ? [...(item.varieties || []), value] : item.varieties || [];
  return { assemblyVarietyMap: map, assemblyTypes, varieties };
}

/** The order-panel key of the row that orders this material (or assembly / variety of it). */
export function orderEntryKey(materialId: string, materialName: string, assemblyType: string, variety: string): string {
  const assembly = assemblyType.trim() || materialName.trim();
  const value = variety.trim();
  if (!value && norm(assembly) === norm(materialName)) return `catalog_${materialId}_base`;
  return `variety_${materialId}_${assembly}_${value || 'base'}`;
}
